/**
 * Confidence Calibration System
 * 
 * Ensures confidence scores accurately reflect actual accuracy.
 * Uses isotonic regression for monotonic calibration.
 * 
 * Key concepts:
 * - Raw confidence: The score output by the classifier
 * - Calibrated confidence: Adjusted to match actual accuracy
 * - ECE (Expected Calibration Error): Measures calibration quality
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// TYPES
// =============================================================================

export interface CalibrationBucket {
  /** Confidence range [min, max) */
  confidenceRange: [number, number];
  /** Total predictions in this bucket */
  totalPredictions: number;
  /** Correct predictions in this bucket */
  correctPredictions: number;
  /** Actual accuracy in this bucket */
  actualAccuracy: number;
  /** Calibration error for this bucket */
  calibrationError: number;
}

export interface CalibrationResult {
  /** Whether the model is well-calibrated (ECE < 0.1) */
  isWellCalibrated: boolean;
  /** Expected Calibration Error */
  expectedCalibrationError: number;
  /** Calibration buckets */
  buckets: CalibrationBucket[];
  /** Adjustment function to calibrate raw confidence */
  adjustmentPoints: Array<{ x: number; y: number }>;
  /** Statistics about the calibration data */
  stats: {
    totalPredictions: number;
    totalCorrect: number;
    overallAccuracy: number;
    dateRange: { start: string; end: string };
  };
}

export interface ConfidenceThresholds {
  /** >= this = auto-approve (no review needed) */
  autoApprove: number;
  /** >= this = high confidence (optional review) */
  highConfidence: number;
  /** < this = needs review */
  lowConfidence: number;
  /** < this = flag for manual review */
  veryLowConfidence: number;
}

export const DEFAULT_THRESHOLDS: ConfidenceThresholds = {
  autoApprove: 0.90,
  highConfidence: 0.80,
  lowConfidence: 0.60,
  veryLowConfidence: 0.40
};

// =============================================================================
// CONFIDENCE CALIBRATOR CLASS
// =============================================================================

export class ConfidenceCalibrator {
  private companyId: string;
  private buckets: CalibrationBucket[] = [];
  private adjustmentPoints: Array<{ x: number; y: number }> = [];
  private isBuilt: boolean = false;
  private numBuckets: number = 10;
  
  constructor(companyId: string, numBuckets: number = 10) {
    this.companyId = companyId;
    this.numBuckets = numBuckets;
  }
  
  /**
   * Build calibration from historical predictions vs outcomes
   */
  async buildCalibration(daysBack: number = 90): Promise<CalibrationResult> {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysBack);
    
    // Get predictions with confidence scores
    const { data: predictions, error: predError } = await supabase
      .from('bank_transactions')
      .select(`
        id,
        classification_confidence,
        classification_method,
        category_type,
        subcategory,
        correction_count
      `)
      .eq('company_id', this.companyId)
      .not('classification_confidence', 'is', null)
      .not('category_type', 'is', null)
      .gte('updated_at', startDate.toISOString())
      .lte('updated_at', endDate.toISOString())
      .order('updated_at', { ascending: false })
      .limit(10000);
    
    if (predError || !predictions || predictions.length === 0) {
      console.error('Error loading calibration data:', predError);
      return this.getEmptyResult(startDate, endDate);
    }
    
    // Get corrections to know which predictions were wrong
    const transactionIds = predictions.map(p => p.id);
    const { data: corrections } = await supabase
      .from('transaction_classification_log')
      .select('transaction_id')
      .in('transaction_id', transactionIds)
      .eq('was_correct', false);
    
    const correctedIds = new Set(corrections?.map(c => c.transaction_id) || []);
    
    // Build calibration buckets
    this.buckets = [];
    const bucketSize = 1.0 / this.numBuckets;
    
    for (let i = 0; i < this.numBuckets; i++) {
      const min = i * bucketSize;
      const max = (i + 1) * bucketSize;
      
      // Find predictions in this bucket
      const inBucket = predictions.filter(p => 
        p.classification_confidence >= min && 
        p.classification_confidence < max
      );
      
      // Count correct predictions (not corrected and no correction_count)
      const correct = inBucket.filter(p => 
        !correctedIds.has(p.id) && 
        (p.correction_count || 0) === 0
      );
      
      const totalPredictions = inBucket.length;
      const correctPredictions = correct.length;
      const actualAccuracy = totalPredictions > 0 
        ? correctPredictions / totalPredictions 
        : 0;
      
      const expectedAccuracy = (min + max) / 2;
      const calibrationError = Math.abs(actualAccuracy - expectedAccuracy);
      
      this.buckets.push({
        confidenceRange: [min, max],
        totalPredictions,
        correctPredictions,
        actualAccuracy,
        calibrationError
      });
    }
    
    // Calculate Expected Calibration Error (ECE)
    const totalPredictions = predictions.length;
    const ece = this.buckets.reduce((sum, bucket) => 
      sum + (bucket.totalPredictions / totalPredictions) * bucket.calibrationError, 
      0
    );
    
    // Build adjustment function using isotonic regression
    this.adjustmentPoints = this.buildIsotonicAdjustment();
    
    this.isBuilt = true;
    
    // Calculate overall stats
    const totalCorrect = predictions.filter(p => 
      !correctedIds.has(p.id) && 
      (p.correction_count || 0) === 0
    ).length;
    
    return {
      isWellCalibrated: ece < 0.1,
      expectedCalibrationError: ece,
      buckets: this.buckets,
      adjustmentPoints: this.adjustmentPoints,
      stats: {
        totalPredictions,
        totalCorrect,
        overallAccuracy: totalCorrect / totalPredictions,
        dateRange: {
          start: startDate.toISOString(),
          end: endDate.toISOString()
        }
      }
    };
  }
  
  /**
   * Build isotonic regression adjustment points
   * Ensures monotonically increasing calibrated confidence
   */
  private buildIsotonicAdjustment(): Array<{ x: number; y: number }> {
    // Filter buckets with enough data
    const validBuckets = this.buckets.filter(b => b.totalPredictions >= 10);
    
    if (validBuckets.length < 3) {
      // Not enough data, return identity mapping
      return [{ x: 0, y: 0 }, { x: 1, y: 1 }];
    }
    
    // Create initial points from bucket centers and actual accuracies
    const points = validBuckets.map(b => ({
      x: (b.confidenceRange[0] + b.confidenceRange[1]) / 2,
      y: b.actualAccuracy,
      weight: b.totalPredictions
    }));
    
    // Apply Pool Adjacent Violators (PAV) algorithm
    const isotonic = this.poolAdjacentViolators(points);
    
    return isotonic.map(p => ({ x: p.x, y: p.y }));
  }
  
  /**
   * Pool Adjacent Violators algorithm for isotonic regression
   */
  private poolAdjacentViolators(
    points: Array<{ x: number; y: number; weight: number }>
  ): Array<{ x: number; y: number; weight: number }> {
    if (points.length === 0) return [];
    
    // Sort by x (confidence)
    const sorted = [...points].sort((a, b) => a.x - b.x);
    
    // Pool violators until monotonic
    let result = sorted.map(p => ({ ...p }));
    let changed = true;
    
    while (changed) {
      changed = false;
      
      for (let i = 0; i < result.length - 1; i++) {
        // Check for monotonicity violation (y should be increasing)
        if (result[i].y > result[i + 1].y) {
          // Pool these two blocks
          const totalWeight = result[i].weight + result[i + 1].weight;
          const pooledY = (
            result[i].y * result[i].weight + 
            result[i + 1].y * result[i + 1].weight
          ) / totalWeight;
          
          result[i] = {
            x: result[i].x, // Keep first x
            y: pooledY,
            weight: totalWeight
          };
          result.splice(i + 1, 1);
          changed = true;
          break;
        }
      }
    }
    
    return result;
  }
  
  /**
   * Calibrate a raw confidence score
   */
  calibrate(rawConfidence: number): number {
    if (!this.isBuilt || this.adjustmentPoints.length < 2) {
      return rawConfidence;
    }
    
    // Linear interpolation between adjustment points
    const points = this.adjustmentPoints;
    
    // Handle edge cases
    if (rawConfidence <= points[0].x) {
      return points[0].y;
    }
    if (rawConfidence >= points[points.length - 1].x) {
      return points[points.length - 1].y;
    }
    
    // Find surrounding points and interpolate
    for (let i = 0; i < points.length - 1; i++) {
      if (rawConfidence >= points[i].x && rawConfidence < points[i + 1].x) {
        const t = (rawConfidence - points[i].x) / (points[i + 1].x - points[i].x);
        return points[i].y + t * (points[i + 1].y - points[i].y);
      }
    }
    
    return rawConfidence;
  }
  
  /**
   * Get calibration status
   */
  get ready(): boolean {
    return this.isBuilt;
  }
  
  /**
   * Get empty result for when no data available
   */
  private getEmptyResult(startDate: Date, endDate: Date): CalibrationResult {
    return {
      isWellCalibrated: true, // Assume well-calibrated with no data
      expectedCalibrationError: 0,
      buckets: [],
      adjustmentPoints: [{ x: 0, y: 0 }, { x: 1, y: 1 }],
      stats: {
        totalPredictions: 0,
        totalCorrect: 0,
        overallAccuracy: 0,
        dateRange: {
          start: startDate.toISOString(),
          end: endDate.toISOString()
        }
      }
    };
  }
  
  /**
   * Store calibration data to database
   */
  async saveCalibration(): Promise<void> {
    if (!this.isBuilt) return;
    
    const today = new Date().toISOString().split('T')[0];
    
    // Save bucket data
    for (let i = 0; i < this.buckets.length; i++) {
      const bucket = this.buckets[i];
      
      try {
        await supabase
          .from('confidence_calibration')
          .upsert({
            company_id: this.companyId,
            calibration_date: today,
            bucket_index: i,
            total_predictions: bucket.totalPredictions,
            correct_predictions: bucket.correctPredictions,
            actual_accuracy: bucket.actualAccuracy
          }, {
            onConflict: 'company_id,calibration_date,bucket_index'
          });
      } catch (error) {
        console.debug('Could not save calibration bucket:', error);
      }
    }
  }
  
  /**
   * Load calibration from database
   */
  async loadCalibration(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('confidence_calibration')
        .select('*')
        .eq('company_id', this.companyId)
        .order('calibration_date', { ascending: false })
        .order('bucket_index', { ascending: true })
        .limit(this.numBuckets);
      
      if (error || !data || data.length === 0) {
        return false;
      }
      
      // Reconstruct buckets
      const bucketSize = 1.0 / this.numBuckets;
      this.buckets = data.map((row: any, i: number) => ({
        confidenceRange: [i * bucketSize, (i + 1) * bucketSize] as [number, number],
        totalPredictions: row.total_predictions,
        correctPredictions: row.correct_predictions,
        actualAccuracy: row.actual_accuracy || 0,
        calibrationError: Math.abs((row.actual_accuracy || 0) - ((i + 0.5) * bucketSize))
      }));
      
      this.adjustmentPoints = this.buildIsotonicAdjustment();
      this.isBuilt = true;
      
      return true;
    } catch (error) {
      console.debug('Could not load calibration:', error);
      return false;
    }
  }
}

// =============================================================================
// ACTION DETERMINATION
// =============================================================================

export type ClassificationAction = 
  | 'auto_approve' 
  | 'high_confidence' 
  | 'needs_review' 
  | 'manual_required';

export interface ActionRecommendation {
  action: ClassificationAction;
  shouldFlag: boolean;
  explanation: string;
  suggestedReviewPriority: 'low' | 'medium' | 'high';
}

/**
 * Determine action based on calibrated confidence
 */
export function determineAction(
  calibratedConfidence: number,
  thresholds: ConfidenceThresholds = DEFAULT_THRESHOLDS
): ActionRecommendation {
  if (calibratedConfidence >= thresholds.autoApprove) {
    return {
      action: 'auto_approve',
      shouldFlag: false,
      explanation: 'High confidence classification - auto-approved',
      suggestedReviewPriority: 'low'
    };
  }
  
  if (calibratedConfidence >= thresholds.highConfidence) {
    return {
      action: 'high_confidence',
      shouldFlag: false,
      explanation: 'High confidence but below auto-approve threshold',
      suggestedReviewPriority: 'low'
    };
  }
  
  if (calibratedConfidence >= thresholds.lowConfidence) {
    return {
      action: 'needs_review',
      shouldFlag: true,
      explanation: 'Medium confidence - recommend review',
      suggestedReviewPriority: 'medium'
    };
  }
  
  if (calibratedConfidence >= thresholds.veryLowConfidence) {
    return {
      action: 'needs_review',
      shouldFlag: true,
      explanation: 'Low confidence - needs review',
      suggestedReviewPriority: 'high'
    };
  }
  
  return {
    action: 'manual_required',
    shouldFlag: true,
    explanation: 'Very low confidence - manual classification required',
    suggestedReviewPriority: 'high'
  };
}

/**
 * Calculate dynamic thresholds based on company's historical accuracy
 */
export async function calculateDynamicThresholds(
  companyId: string,
  calibrator: ConfidenceCalibrator
): Promise<ConfidenceThresholds> {
  if (!calibrator.ready) {
    return DEFAULT_THRESHOLDS;
  }
  
  // Get recent accuracy statistics
  const { data: stats } = await supabase
    .from('bank_transactions')
    .select('classification_confidence, correction_count')
    .eq('company_id', companyId)
    .not('classification_confidence', 'is', null)
    .order('updated_at', { ascending: false })
    .limit(1000);
  
  if (!stats || stats.length < 100) {
    return DEFAULT_THRESHOLDS;
  }
  
  // Calculate accuracy at different thresholds
  const calculateAccuracyAboveThreshold = (threshold: number): number => {
    const aboveThreshold = stats.filter(s => s.classification_confidence >= threshold);
    if (aboveThreshold.length === 0) return 0;
    const correct = aboveThreshold.filter(s => (s.correction_count || 0) === 0);
    return correct.length / aboveThreshold.length;
  };
  
  // Find threshold that gives 95% accuracy for auto-approve
  let autoApprove = 0.95;
  for (let t = 0.95; t >= 0.80; t -= 0.05) {
    if (calculateAccuracyAboveThreshold(t) >= 0.95) {
      autoApprove = t;
      break;
    }
  }
  
  // Find threshold that gives 85% accuracy for high confidence
  let highConfidence = 0.85;
  for (let t = 0.85; t >= 0.70; t -= 0.05) {
    if (calculateAccuracyAboveThreshold(t) >= 0.85) {
      highConfidence = t;
      break;
    }
  }
  
  return {
    autoApprove,
    highConfidence,
    lowConfidence: Math.max(0.50, highConfidence - 0.20),
    veryLowConfidence: Math.max(0.30, highConfidence - 0.40)
  };
}

// =============================================================================
// SINGLETON INSTANCE MANAGEMENT
// =============================================================================

const calibratorInstances = new Map<string, ConfidenceCalibrator>();

/**
 * Get or create calibrator for a company
 */
export async function getCalibrator(companyId: string): Promise<ConfidenceCalibrator> {
  if (!calibratorInstances.has(companyId)) {
    const calibrator = new ConfidenceCalibrator(companyId);
    
    // Try to load from database first
    const loaded = await calibrator.loadCalibration();
    
    if (!loaded) {
      // Build fresh calibration
      await calibrator.buildCalibration(90);
      await calibrator.saveCalibration();
    }
    
    calibratorInstances.set(companyId, calibrator);
  }
  
  return calibratorInstances.get(companyId)!;
}

/**
 * Clear cached calibrator (e.g., after recalibration)
 */
export function clearCalibrator(companyId: string): void {
  calibratorInstances.delete(companyId);
}
